<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import Button from "../Button.vue";
import StatusChip from "../StatusChip.vue";
import AppointmentPanel from "./AppointmentPanel.vue";
import BookModal from "./BookModal.vue";
import { schedulingApi, type Appointment } from "../../api/scheduling";
import type { ConversationDetail } from "../../api/types";
import { can } from "../../composables/permissions";
import {
  onAppointmentChange,
  statusLabel,
  statusTone,
  whenText,
} from "../../composables/agenda";
import { t } from "../../i18n";

/**
 * "Agendamentos" in the contact panel (decision 0022): what is coming first, then a few past
 * ones; a tap opens the appointment over the panel, and "+ Agendar" books one for this contact.
 */
const props = withDefaults(
  defineProps<{
    slug: string;
    contactId: string;
    contactName: string;
    /** The open conversation, when the panel is beside one. */
    conversation?: ConversationDetail | null;
  }>(),
  { conversation: null },
);
const PAST = 3;
const list = ref<Appointment[]>([]);
const loaded = ref(false);
const openId = ref<string | null>(null);
const booking = ref(false);
const showPast = ref(false);

async function load() {
  try {
    list.value = (
      await schedulingApi.contactAppointments(props.slug, props.contactId)
    ).appointments;
  } catch {
    list.value = [];
  } finally {
    loaded.value = true;
  }
}
watch(
  () => props.contactId,
  () => {
    openId.value = null;
    loaded.value = false;
    void load();
  },
);
let stop: (() => void) | undefined;
let timer: ReturnType<typeof setTimeout> | undefined;
onMounted(() => {
  void load();
  stop = onAppointmentChange(() => {
    clearTimeout(timer);
    timer = setTimeout(() => void load(), 300);
  });
});
onBeforeUnmount(() => {
  stop?.();
  clearTimeout(timer);
});

const now = () => Date.now();
const upcoming = computed(() =>
  list.value
    .filter(
      (a) => new Date(a.endsAt).getTime() >= now() && a.status !== "cancelled",
    )
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt)),
);
const past = computed(() =>
  list.value
    .filter((a) => !upcoming.value.includes(a))
    .sort((a, b) => b.startsAt.localeCompare(a.startsAt)),
);
const shownPast = computed(() =>
  showPast.value ? past.value : past.value.slice(0, PAST),
);
/** The conversation's 24-hour window, for how the confirmation goes. */
const windowOpen = computed(() => {
  const c = props.conversation;
  if (!c) return null;
  const ends = [c.sessionWindowEndsAt, c.freeWindowEndsAt]
    .filter((x): x is string => Boolean(x))
    .map((x) => new Date(x).getTime());
  return ends.some((x) => x > now());
});
function booked() {
  booking.value = false;
  void load();
}
</script>
<template>
  <section class="detail-section contact-appointments">
    <div class="row">
      <h3 class="contact-section-title grow">
        {{ t("appointment.contact_title") }}
      </h3>
      <Button
        v-if="can(slug, 'appointments.write')"
        variant="ghost"
        compact
        icon="plus"
        @click="booking = true"
        >{{ t("appointment.book") }}</Button
      >
    </div>
    <p v-if="loaded && !list.length" class="muted small">
      {{ t("appointment.contact_empty") }}
    </p>
    <button
      v-for="a in [...upcoming, ...shownPast]"
      :key="a.id"
      type="button"
      class="contact-history-row"
      :class="{ past: !upcoming.includes(a) }"
      @click="openId = a.id"
    >
      <span class="grow"
        ><strong>{{ whenText(a) }}</strong
        ><br /><small class="muted"
          >{{ a.typeName
          }}<template v-if="a.memberName">
            · {{ a.memberName }}</template
          ></small
        ></span
      >
      <StatusChip
        :status="statusTone(a.status)"
        :label="statusLabel(a.status)"
      />
    </button>
    <button
      v-if="past.length > PAST"
      type="button"
      class="link-button small"
      @click="showPast = !showPast"
    >
      {{
        showPast
          ? t("appointment.fewer_past")
          : t("appointment.more_past", { count: past.length - PAST })
      }}
    </button>
    <Teleport to="body">
      <AppointmentPanel
        v-if="openId"
        :slug="slug"
        :appointment-id="openId"
        overlay
        @close="openId = null"
        @changed="load"
      />
    </Teleport>
    <BookModal
      :open="booking"
      :slug="slug"
      :contact="{ id: contactId, name: contactName }"
      :conversation-id="conversation?.id ?? null"
      :window-open="windowOpen"
      @close="booking = false"
      @booked="booked"
    />
  </section>
</template>
